import { useCallback, useEffect, useReducer } from 'react';
import { useFetcher } from 'react-router';
import TrackingActions, {
  TRACKING_ACTIONS,
  TRACKING_CATEGORIES,
  TRACKING_EVENTS,
  TRACKING_LABELS,
} from '@cx-web/feature-tracking';
import useMedia from '@cx-web/ui-shell/use-media';

import { ModalView } from '~/components/composites/legacy-auth-modal';
import { promisifiedDelay } from '~/utils/promisifiedDelay';

import onboardingReducer from './reducers';
import {
  HANDLE_INPUT_BLUR,
  HANDLE_INPUT_CHANGE,
  type OnboardingProps,
  onboardingVariants,
  onboardingView,
  TOGGLE_AGREE_TERMS,
  UPDATE_SUBMIT_STATE,
} from './types';

export function useOnboardingActions({
  variant,
  view = onboardingView.SECONDARY,
}: Pick<OnboardingProps, 'variant' | 'view'>) {
  const fetcher = useFetcher<{
    fieldErrors?: { email: String };
    message?: string;
    success: boolean;
  }>();

  const { isDesktop, isMobile } = useMedia();

  // UI vars

  const isSecondaryView = view === onboardingView.SECONDARY;
  const shouldRenderAsSecondary = isMobile || isSecondaryView;
  const shouldRenderAsMyOCLoginCTA = isSecondaryView && variant === onboardingVariants.USER_SIGNUP;

  const mobileAndSecondary = !isDesktop || isSecondaryView;
  const desktopAndPrimary = isDesktop && !isSecondaryView;

  // State

  const [{ agreeTerms, errorOnBlur, input, submitted }, dispatch] = useReducer(onboardingReducer, {
    agreeTerms: false,
    errorOnBlur: false,
    input: { isValid: false, value: '' },
    submitted: false,
  });

  const inputIsValid = input.isValid;
  const canSubmit = shouldRenderAsMyOCLoginCTA ? inputIsValid : inputIsValid && agreeTerms;
  const disabled = !canSubmit || fetcher.state === 'loading' || submitted;

  // Helpers

  const toggleTerms = useCallback(() => {
    dispatch({ type: TOGGLE_AGREE_TERMS });
  }, [dispatch]);

  const handleInputChange = useCallback(
    (value: string = '') => {
      const regex = /\S+@\S+\.\S+/;
      const isValidEmail = regex.test(value);
      const payload = {
        value,
        isValid: isValidEmail,
      };
      dispatch({ type: HANDLE_INPUT_CHANGE, payload });
    },
    [dispatch],
  );

  const handleInputBlur = useCallback(() => {
    dispatch({ type: HANDLE_INPUT_BLUR });
  }, [dispatch]);

  const updateSubmitState = useCallback(
    (state: boolean) => {
      dispatch({ type: UPDATE_SUBMIT_STATE, submit: state });
    },
    [dispatch],
  );

  const handleFormSubmit = useCallback(
    async (email: string) => {
      const trackingService = new TrackingActions();
      trackingService.eventTracking({
        event: TRACKING_EVENTS.SIGN_UP_CONTAINER,
        eventAction: TRACKING_ACTIONS.signupContainer,
        eventCategory: TRACKING_CATEGORIES.ODDSCHECKER_ACCOUNT,
        eventLabel: TRACKING_LABELS.signupContainer,
      });

      fetcher.submit({ email }, { method: 'POST', action: '/api/cx/myoc/newsletter' });
    },
    [fetcher],
  );

  useEffect(() => {
    const { data, state } = fetcher;
    if (state !== 'idle' || !data) return;

    if (data.success) {
      updateSubmitState(true);
      promisifiedDelay(5000).then(() => {
        handleInputChange('');
        updateSubmitState(false);
      });
    } else {
      console.log('Onboarding error: ', data.fieldErrors);
      updateSubmitState(false);
    }
  }, [fetcher, handleInputChange, updateSubmitState]);

  const updateMyOcEmailInputWithRetries = useCallback(
    async (i = 0) => {
      if (i > 3) return;

      const myocLogin = document.querySelector("div[id='myoc-login-modal']");
      const emailInput = myocLogin?.querySelector('input[type=email]');

      if (emailInput) {
        (emailInput as HTMLInputElement).value = input.value;
        return;
      }

      await promisifiedDelay(500);
      updateMyOcEmailInputWithRetries((i += 1));
    },
    [input.value],
  );

  const handleSubmit = useCallback(async (): Promise<void> => {
    if (shouldRenderAsMyOCLoginCTA) {
      try {
        window.openModalAtView(ModalView.SignupForm);

        await promisifiedDelay(500);

        updateMyOcEmailInputWithRetries();
      } catch (err) {
        console.log('error at handleSubmit');
        console.log(err);
      }
      return;
    }

    handleFormSubmit(input.value);
  }, [handleFormSubmit, input.value, shouldRenderAsMyOCLoginCTA, updateMyOcEmailInputWithRetries]);

  return {
    agreeTerms,
    disabled,
    errorOnBlur,
    input,
    submitted,
    inputIsValid,
    canSubmit,
    fetcher,
    mobileAndSecondary,
    desktopAndPrimary,
    shouldRenderAsSecondary,
    shouldRenderAsMyOCLoginCTA,
    toggleTerms,
    handleInputChange,
    handleInputBlur,
    handleSubmit,
  };
}
